/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import ModalHalf from '@components/modal/ModalHalf';
import Button from '@components/form/Button';
import RadioButton from '@components/form/RadioButton';
import SociollaText from '@components/sociolla/SociollaText';

const roleRadios = [
  { label: 'Salesman', value: 'salesman' },
  { label: 'Merchandiser', value: 'merchandiser' },
  { label: 'Beauty Advisor', value: 'beauty_advisor' },
  { label: 'Promotor', value: 'promotor' },
  { label: 'Collector', value: 'collector' },
];

const allActivityRadios = [
  { label: 'Visit Existing Customer', value: 'visiting_existing_customer' },
  { label: 'Invoice Collection', value: 'invoice_collection' },
  { label: 'Engage Potential Customer', value: 'engage_potential_customers' },
];

const CreateNewPlan = ({
  show,
  onClose,
  onSubmit,
  selectedRole,
  showRoleSelection,
}) => {
  const userData = useSelector((state) => state.auth.user);
  const [planRole, setPlanRole] = useState('');
  const [planType, setPlanType] = useState('');

  const effectiveRole = showRoleSelection
    ? planRole
    : selectedRole || userData?.role?.code || '';

  const isBa = effectiveRole === 'beauty_advisor';
  const isPromotor = effectiveRole === 'promotor';
  const isCollector = effectiveRole === 'collector';

  const activityRadios = isBa
    ? allActivityRadios.filter((r) => r.value === 'visiting_existing_customer')
    : isPromotor
    ? [{ label: 'Event Activity', value: 'event_activity' }]
    : isCollector
    ? [{ label: 'Invoice Collection', value: 'invoice_collection' }]
    : allActivityRadios;

  useEffect(() => {
    let mounted = true;
    if (mounted && show) {
      setPlanRole('');
      if (isBa) {
        setPlanType('visiting_existing_customer');
      } else if (isPromotor) {
        setPlanType('event_activity');
      } else if (isCollector) {
        setPlanType('invoice_collection');
      } else {
        setPlanType('');
      }
    }
    return () => {
      mounted = false;
    };
  }, [show]);

  // Auto-select for BA, Promotor, and Collector
  useEffect(() => {
    if (isBa) {
      setPlanType('visiting_existing_customer');
    } else if (isPromotor) {
      setPlanType('event_activity');
    } else if (isCollector) {
      setPlanType('invoice_collection');
    } else {
      setPlanType('');
    }
  }, [effectiveRole]);

  const openForm = (val = null) => {
    const activityType = val || planType;
    if (!activityType) {
      return;
    }
    if (showRoleSelection) {
      onSubmit({ activityType, role: planRole });
    } else {
      onSubmit(activityType);
    }
  };

  const handleActivityChange = (value) => {
    if (isWeb || showRoleSelection) {
      setPlanType(value);
    } else {
      openForm(value);
    }
  };

  const showNextButton =
    isWeb || showRoleSelection || isBa || isPromotor || isCollector;

  const isNextEnabled = showRoleSelection
    ? !!planRole && !!planType
    : !!planType;

  return (
    <ModalHalf
      testID="sales-activity-create-plan-modal"
      title="Plan New Activity"
      visible={show}
      onClose={() => onClose()}
      maxWidth={isWeb ? 350 : '100%'}
      spaceFromTop={isWeb ? '10%' : false}
      headerBordered={true}>
      <View style={styles.container}>
        {/* Role Selection */}
        {showRoleSelection && (
          <View style={styles.section}>
            <SociollaText style={styles.sectionLabel}>
              Choose assigned role:
            </SociollaText>
            <RadioButton
              testID="sales-activity-select-role-type"
              orientation="vertical"
              value={planRole}
              data={roleRadios}
              onChange={(value) => setPlanRole(value)}
            />
          </View>
        )}

        {/* Divider */}
        {showRoleSelection && <View style={styles.divider} />}

        {/* Activity Type Selection */}
        <View style={styles.section}>
          <SociollaText style={styles.sectionLabel}>
            Choose activity type:
          </SociollaText>
          <RadioButton
            testID="sales-activity-create-plan-type"
            orientation="vertical"
            value={planType}
            data={activityRadios}
            onChange={handleActivityChange}
          />
        </View>

        {/* Next Button */}
        {showNextButton && (
          <View style={styles.footer}>
            <Button
              testID="sales-activity-create-plan-next-btn"
              rounded={false}
              buttonStyle={[
                styles.nextButton,
                {
                  backgroundColor: isNextEnabled ? Colors.blue0 : Colors.grey2,
                },
              ]}
              title={
                <SociollaText type="bold" style={styles.nextButtonText}>
                  Next
                </SociollaText>
              }
              onPress={() => openForm()}
            />
          </View>
        )}
      </View>
    </ModalHalf>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingTop: 4,
  },
  section: {
    width: '100%',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  sectionLabel: {
    fontSize: 14,
    marginBottom: 12,
    color: Colors.black,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: Colors.grey0,
  },
  footer: {
    width: '100%',
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 24,
  },
  nextButton: {
    width: '100%',
    height: 40,
    borderRadius: 10,
  },
  nextButtonText: {
    fontSize: 15,
    letterSpacing: 0.5,
    color: Colors.white,
  },
});

export default React.memo(CreateNewPlan, (prev, next) => prev === next);
